import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import BuyCrypto from './pages/BuyCrypto';
import ExchangeHistory from './pages/ExchangeHistory';
import TransactionStatusPage from './pages/TransactionStatusPage';
import AuthPage from './pages/AuthPage';
import NotFound from './pages/NotFound';

// New Pages
import MarketPage from './pages/market/MarketPage';
import ApiPage from './pages/api/ApiPage';
import WidgetPage from './pages/widget/WidgetPage';
import MobilePage from './pages/mobile/MobilePage';
import ReferralPage from './pages/referral/ReferralPage';
import AboutPage from './pages/company/AboutPage';
import CareersPage from './pages/company/CareersPage';
import HelpPage from './pages/support/HelpPage';
import FAQPage from './pages/support/FAQPage';
import ConnectWalletPage from './pages/wallet/ConnectWalletPage';
import { TermsPage, PrivacyPage, CookiesPage, AMLKYCPage } from './pages/legal/LegalPages';

import './App.css';

// Simple page component for pages that share a similar layout
const SimplePage = ({ title, subtitle, icon: Icon, features = [] }) => (
  <div className="simple-page">
    <div className="simple-container">
      <div className="simple-hero">
        {Icon && <Icon size={64} className="simple-icon" />}
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {features.length > 0 && (
        <div className="simple-features">
          {features.map((feature, index) => (
            <div key={index} className="simple-feature">
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

function App() {
  return (
    <div className="app">
      <Header />
      <main className="main-content">
        <Routes>
          {/* Auth Pages */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          
          {/* Main Pages - Allow guests */}
          <Route path="/" element={<Home />} />
          <Route path="/buy" element={<BuyCrypto />} />
          <Route path="/sell" element={<BuyCrypto />} />
          <Route path="/market" element={<MarketPage />} />
          
          {/* Exchange Pages - Allow guests for transactions */}
          <Route path="/tx/:id" element={
            <ProtectedRoute allowGuest={true}>
              <TransactionStatusPage />
            </ProtectedRoute>
          } />
          
          {/* History - Allow guests to view their transaction history */}
          <Route path="/history" element={
            <ProtectedRoute allowGuest={true}>
              <ExchangeHistory />
            </ProtectedRoute>
          }/>
          
          {/* Product Pages */}
          <Route path="/api" element={<ApiPage />} />
          <Route path="/widget" element={<WidgetPage />} />
          <Route path="/mobile" element={<MobilePage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/affiliate" element={<ReferralPage />} />
          <Route path="/white-label" element={
            <SimplePage 
              title="White Label Solution"
              subtitle="Launch your own branded cryptocurrency exchange platform with our comprehensive white-label solution."
              features={[
                { title: 'Custom Branding', desc: 'Your logo, your colors, your domain' },
                { title: 'Full Control', desc: 'Manage rates, fees, and supported currencies' },
                { title: 'Fast Setup', desc: 'Launch in days, not months' },
                { title: '24/7 Support', desc: 'Dedicated technical support team' },
              ]}
            />
          } />
          
          {/* Company Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/blog" element={
            <SimplePage 
              title="Blog"
              subtitle="Latest news, updates, and insights from the Omini Bridge team."
            />
          } />
          <Route path="/press" element={
            <SimplePage 
              title="Press Kit"
              subtitle="Media resources and brand assets for journalists and partners."
            />
          } />
          <Route path="/partners" element={
            <SimplePage 
              title="Partners"
              subtitle="Explore our ecosystem of partners and integrations."
              features={[
                { title: 'Wallet Providers', desc: 'Integrate with leading wallets' },
                { title: 'Payment Processors', desc: 'Multiple payment options' },
                { title: 'Blockchain Networks', desc: 'Support for major chains' },
                { title: 'Security Partners', desc: 'Industry-leading security' },
              ]}
            />
          } />
          
          {/* Support Pages */}
          <Route path="/help" element={<HelpPage />} />
          <Route path="/contact" element={
            <SimplePage 
              title="Contact Us"
              subtitle="Get in touch with our support team. We're here to help 24/7."
              features={[
                { title: 'Email', desc: 'support@omnibridge.com' },
                { title: 'Live Chat', desc: 'Available 24/7' },
                { title: 'Phone', desc: '+1 (888) 123-4567' },
                { title: 'Social', desc: '@OminiBridge' },
              ]}
            />
          } />
          <Route path="/status" element={
            <SimplePage 
              title="System Status"
              subtitle="All systems operational"
              features={[
                { title: 'Exchange API', desc: 'Operational' },
                { title: 'Wallet Services', desc: 'Operational' },
                { title: 'Payment Processing', desc: 'Operational' },
                { title: 'Customer Support', desc: 'Operational' },
              ]}
            />
          } />
          <Route path="/supported-coins" element={
            <SimplePage 
              title="Supported Currencies"
              subtitle="Browse the complete list of 1000+ supported cryptocurrencies."
            />
          } />
          <Route path="/list-a-coin" element={
            <SimplePage 
              title="List a Coin"
              subtitle="Get your cryptocurrency or token listed on Omini Bridge."
              features={[
                { title: 'Application Review', desc: 'Submit your project for evaluation' },
                { title: 'Technical Integration', desc: 'We handle the technical setup' },
                { title: 'Marketing Support', desc: 'Promote your listing' },
                { title: 'Ongoing Support', desc: 'Dedicated account manager' },
              ]}
            />
          } />
          <Route path="/bug-bounty" element={
            <SimplePage 
              title="Bug Bounty Program"
              subtitle="Report security vulnerabilities and earn rewards."
              features={[
                { title: 'Rewards', desc: 'Up to $10,000 for critical bugs' },
                { title: 'Scope', desc: 'Web, API, and mobile apps' },
                { title: 'Fast Response', desc: 'Initial response within 24h' },
                { title: 'Safe Harbor', desc: 'Legal protection for researchers' },
              ]}
            />
          } />
          <Route path="/faq" element={<FAQPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/aml-kyc" element={<AMLKYCPage />} />
          
          {/* Other Pages */}
          <Route path="/connect" element={<ConnectWalletPage />} />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
